(function () {
  if (window.LPBHeroConfetti) return;

  const instances = new WeakMap();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const COLORS = ['#ffd54d', '#f2b134', '#d84b3d', '#1d8f6a', '#4aa3df', '#f06a92', '#ffffff'];

  class HeroConfetti {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('.lpb-hero__confetti-canvas');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.particles = [];
      this.animationFrame = null;
      this.resizeObserver = null;
      this.startTime = 0;
      this.lastFrame = 0;
      this.duration = 5200;
      this.fadeOutMs = 1200;

      if (!this.canvas || !this.ctx || prefersReducedMotion.matches) return;

      this.handleResize = this.handleResize.bind(this);
      this.animate = this.animate.bind(this);

      this.setupCanvas();
      this.createParticles();
      this.observeResize();
      this.play();
    }

    setupCanvas() {
      const rect = this.root.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      this.width = Math.max(Math.round(rect.width), 1);
      this.height = Math.max(Math.round(rect.height), 1);
      this.dpr = dpr;

      this.canvas.width = this.width * dpr;
      this.canvas.height = this.height * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    observeResize() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.root);
      } else {
        window.addEventListener('resize', this.handleResize);
      }
    }

    handleResize() {
      this.setupCanvas();
      this.createParticles();
      this.play();
    }

    createParticles() {
      const isMobile = window.innerWidth <= 770;
      const density = isMobile ? 200 : 320;

      this.particles = Array.from({ length: density }, (_, index) => {
        const depth = 0.45 + Math.random() * 1.35;
        const isStreamer = index % 5 === 0;
        const spawnFromLeft = index % 2 === 0;

        return {
          color: COLORS[index % COLORS.length],
          shape: isStreamer ? 'streamer' : Math.random() > 0.22 ? 'rect' : 'circle',
          depth,
          width: (isStreamer ? 5 : 7) + Math.random() * 10,
          height: (isStreamer ? 26 : 8) + Math.random() * 18,
          x: spawnFromLeft ? -this.width * (0.12 + Math.random() * 0.08) : this.width * (1.12 + Math.random() * 0.08),
          y: -this.height * (0.35 + Math.random() * 0.55),
          vx: (spawnFromLeft ? 1 : -1) * (60 + Math.random() * 180) * depth,
          vy: 120 + Math.random() * 210 * depth,
          gravity: 260 + Math.random() * 280,
          drag: 0.992 - Math.random() * 0.008,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: ((Math.random() * 2.6) + 0.8) * (Math.random() > 0.5 ? 1 : -1),
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: 1.8 + Math.random() * 3.2,
          wobbleAmp: 8 + Math.random() * 22,
          tilt: Math.random() * Math.PI,
          opacity: 0.82 + Math.random() * 0.18
        };
      });
    }

    play() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.startTime = performance.now();
      this.lastFrame = this.startTime;
      this.animationFrame = requestAnimationFrame(this.animate);
    }

    animate(now) {
      const elapsed = now - this.startTime;
      const delta = Math.min((now - this.lastFrame) / 1000, 0.033);
      this.lastFrame = now;

      this.ctx.clearRect(0, 0, this.width, this.height);

      const fadeStart = this.duration - this.fadeOutMs;
      const globalAlpha = elapsed > fadeStart ? Math.max(0, 1 - ((elapsed - fadeStart) / this.fadeOutMs)) : 1;

      for (const particle of this.particles) {
        particle.vy += particle.gravity * delta;
        particle.vx *= particle.drag;
        particle.x += particle.vx * delta;
        particle.y += particle.vy * delta;
        particle.rotation += particle.rotationSpeed * delta;
        particle.wobble += particle.wobbleSpeed * delta;

        const perspectiveScale = 0.55 + particle.depth * 0.7;
        const wobbleX = Math.sin(particle.wobble) * particle.wobbleAmp;
        const wobbleY = Math.cos(particle.wobble * 1.2) * 4;
        const drawX = particle.x + wobbleX;
        const drawY = particle.y + wobbleY;

        if (drawY > this.height + 40 || drawX < -this.width * 0.35 || drawX > this.width * 1.35) {
          particle.y = -30 - Math.random() * this.height * 0.25;
          particle.x = Math.random() * this.width;
          particle.vy = 80 + Math.random() * 160;
        }

        this.ctx.save();
        this.ctx.globalAlpha = particle.opacity * globalAlpha;
        this.ctx.translate(drawX, drawY);
        this.ctx.rotate(particle.rotation);
        this.ctx.scale(perspectiveScale, Math.max(0.25, Math.abs(Math.cos(particle.rotation + particle.tilt))));
        this.ctx.fillStyle = particle.color;

        if (particle.shape === 'circle') {
          this.ctx.beginPath();
          this.ctx.arc(0, 0, particle.width * 0.45, 0, Math.PI * 2);
          this.ctx.fill();
        } else if (particle.shape === 'streamer') {
          this.ctx.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height);
          this.ctx.fillStyle = particle.color;
          this.ctx.fillRect(-1, particle.height / 2, 2, particle.height * 0.8);
        } else {
          this.ctx.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height);
        }

        this.ctx.restore();
      }

      if (elapsed < this.duration) {
        this.animationFrame = requestAnimationFrame(this.animate);
      } else {
        this.ctx.clearRect(0, 0, this.width, this.height);
        this.animationFrame = null;
      }
    }

    destroy() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.handleResize);
      }

      if (this.ctx) {
        this.ctx.clearRect(0, 0, this.width, this.height);
      }
    }
  }

  function initRoot(root) {
    if (!root || instances.has(root) || !root.classList.contains('lpb-hero--confetti-enabled')) {
      return;
    }

    instances.set(root, new HeroConfetti(root));
  }

  function destroyRoot(root) {
    const instance = instances.get(root);

    if (!instance) return;

    instance.destroy();
    instances.delete(root);
  }

  function initAll(scope) {
    const container = scope || document;
    container.querySelectorAll('.lpb-hero--confetti-enabled').forEach(initRoot);
  }

  function handleSectionEvent(event, callback) {
    const section = event.target;
    if (!section || !section.querySelectorAll) return;

    section.querySelectorAll('.lpb-hero--confetti-enabled').forEach(callback);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    handleSectionEvent(event, initRoot);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    handleSectionEvent(event, destroyRoot);
  });

  window.LPBHeroConfetti = {
    initAll,
    destroyRoot
  };
})();
